export const STOAT_HOST = "stoat.chat";
const STOAT_API = "https://api.stoat.chat";

/** App `stoat.json` endpoint format */
export interface AppConfig {
  api: string;
}

/**
 * Fetch env var by name, optionally only when in dev mode.
 * Also prevents compiler from optimizing out injected strings in Docker
 */
const getEnv = (name: string, devOnly?: boolean) =>
  !devOnly || import.meta.env.DEV
    ? (import.meta.env[name] as string)
    : undefined;

/** If host is Stoat, normalize to STOAT_HOST, else return host */
export const normalizeHost = (host: string) =>
  [
    // historically...
    "api.revolt.chat",
    "beta.revolt.chat",
    "revolt.chat",
    // ... and now:
    "api.stoat.chat",
    "beta.stoat.chat",
  ].includes(host)
    ? STOAT_HOST
    : host;

const isStoatOfficialAPI = (api: string) =>
  [
    "https://api.revolt.chat",
    "https://api.stoat.chat",
    "https://stoat.chat/api",
    "https://beta.stoat.chat/api",
    "canary-api.stoat.chat",
  ].includes(api);

const DEFAULT_HOST = normalizeHost(
  getEnv("VITE_DEV_HOST", true) || getEnv("VITE_HOST") || STOAT_HOST,
);

const DEFAULT_API_URL =
  getEnv("VITE_DEV_API_URL", true) || getEnv("VITE_API_URL") || STOAT_API;

if (!isStoatOfficialAPI(DEFAULT_API_URL) && DEFAULT_HOST === STOAT_HOST)
  console.error("VITE_HOST required when VITE_API_URL is set!");

export default {
  /** Default instance (without the protocol) */
  DEFAULT_HOST,
  /** API URL of default instance */
  DEFAULT_API_URL,
  /** WS server override for development */
  DEV_WS_URL: getEnv("VITE_DEV_WS_URL"),
  /** Media server override for development */
  DEV_MEDIA_URL: getEnv("VITE_DEV_MEDIA_URL"),
  /** Proxy server override for development */
  DEV_PROXY_URL: getEnv("VITE_DEV_PROXY_URL"),
  /** Gifbox server override for development */
  DEV_GIFBOX_URL: getEnv("VITE_DEV_GIFBOX_URL"),
  /**
   * RNNoise worklet CDN host location. Defaults to blank, which uses the url provided by the livekit-rnnoise-processor package.
   * Must end with a trailing slash. For offline deployments point it at the copy served by this app (`/rnnoise/`).
   */
  RNNOISE_WORKLET_CDN_URL: getEnv("VITE_RNNOISE_WORKLET_CDN_URL"),
  /**
   * Base URL for unicode emoji packs (`<base>/<pack>/<codepoint>.svg`).
   * Defaults to the upstream CDN; offline deployments serve the packs themselves (e.g. `/emoji`).
   */
  EMOJI_BASE_URL: getEnv("VITE_EMOJI_BASE_URL"),
  /**
   * Changelog feed (`.../v1/changelogs/latest`). Empty disables the startup fetch and the
   * "What's New" entry — the upstream feed lives on the internet.
   */
  CHANGELOG_URL: getEnv("VITE_CHANGELOG_URL"),
  /**
   * Geo-blocking lookup used by the age gate on NSFW channels (upstream: https://geo.revolt.chat).
   * Empty disables the lookup: no channel is treated as region-restricted.
   */
  GEOBLOCK_URL: getEnv("VITE_GEOBLOCK_URL"),
  /**
   * Hide the GIF picker entirely (the GIF service needs internet access).
   */
  GIFS_DISABLED: getEnv("VITE_GIFS_DISABLED") === "true",
  /**
   * Display name of this deployment (third-party instances must not present Stoat branding).
   */
  APP_NAME: getEnv("VITE_APP_NAME") || "Stoat",
  /**
   * Where the complete corresponding source of this build is served (AGPL-3.0 §13).
   */
  SOURCE_URL: getEnv("VITE_SOURCE_URL") || "https://github.com/stoatchat/for-web",
  /**
   * Stellaria integration gateway base URL. When set, sign-in is delegated to Stellaria:
   * the login form is replaced by the SSO hand-off page and account credentials are
   * managed by the gateway (see docs/AUTHENTICATION.md in the deployment repository).
   */
  STELLARIA_GATEWAY_URL: getEnv("VITE_STELLARIA_GATEWAY_URL"),
  /**
   * Space-separated origins allowed to deliver an SSO hand-off code via postMessage.
   */
  STELLARIA_PARENT_ORIGINS: getEnv("VITE_STELLARIA_PARENT_ORIGINS"),
  /**
   * Where to send a user who opened the app directly without a session.
   */
  STELLARIA_CONSOLE_URL: getEnv("VITE_STELLARIA_CONSOLE_URL"),
  /**
   * Session ID to set during development.
   */
  DEVELOPMENT_SESSION_ID: getEnv("VITE_SESSION_ID", true),
  /**
   * Token to set during development.
   */
  DEVELOPMENT_TOKEN: getEnv("VITE_TOKEN", true),
  /**
   * User ID to set during development.
   */
  DEVELOPMENT_USER_ID: getEnv("VITE_USER_ID", true),
};
